// acceptanceVerifier.js — wire-dead-engines E2-WS1-T01.
// @arch:pure-engine
//
// Kiro "task ↔ test traceability": turn a task's acceptance criterion from advisory PROSE
// (today it's only injected as context — the model self-reports DONE) into an EXECUTABLE
// gate that feeds the SAME oracle E1 wired. "honest-green" stops meaning "the run executed"
// and starts meaning "the asked-for behavior verified".
//
// PURE (Tier 1 lib purity): no react, no window, no IPC. The shell port (`runCommand`),
// `root`, and package.json `scripts` are injected by the caller (E3's executor seam). Reuses
// the tested runCheck (verifyChecker) + cmdForScript (verifierFactories) primitives.
//
// SHAPE NORMALIZATION: renderer acceptance is a single STRING (specModel.js), backend is a
// STRING[] (apolo-api/types/task.js). normalizeAcceptance handles both.
//
// SAFE FALLBACK: a criterion that names no runnable check ⇒ contributes nothing; if NOTHING
// in the acceptance is runnable, compileAcceptance returns null and the caller falls back to
// E1's project verifiers (never a false block).

import { runCheck } from './selfRepair/verifyChecker.js';
import { cmdForScript } from './oracle/verifierFactories.js';
import { describeVerifier } from './runtime/verifierDescriptor.js';

/** Normalize task.acceptance (string | string[] | undefined) to a clean string[]. */
export function normalizeAcceptance(task) {
  const a = task && task.acceptance;
  if (Array.isArray(a)) return a.map((s) => String(s ?? '').trim()).filter(Boolean);
  if (typeof a === 'string' && a.trim()) return [a.trim()];
  return [];
}

// A bare command token at the start of a criterion ⇒ run the criterion verbatim.
const BARE_CMD = /^(npm |npx |yarn |pnpm |vitest|eslint|tsc\b|jest|pytest|go test|cargo |make )/i;
// "<prefix>: <command>" form, e.g. "smoke: npm test", "run: npx vitest foo".
const PREFIXED_CMD = /^(?:run|cmd|command|smoke|verify|test)\s*:\s*(.+)$/i;

/**
 * Extract a runnable command from one acceptance criterion, or null.
 * Order: explicit "prefix: cmd" → bare command token → keyword→npm script (needs scripts).
 */
export function extractCommand(criterion, scripts) {
  const c = String(criterion ?? '').trim();
  if (!c) return null;

  const pref = c.match(PREFIXED_CMD);
  if (pref && pref[1].trim()) {
    const inner = pref[1].trim();
    // "test: tests pass" is prose, not a command — only accept if it looks runnable.
    if (BARE_CMD.test(inner)) return inner;
  }
  if (BARE_CMD.test(c)) return c;

  // keyword → npm script (only when that script actually exists → never a false red)
  if (/\blint\b/i.test(c)) { const cmd = cmdForScript('lint', scripts); if (cmd) return cmd; }
  if (/\b(typecheck|types?)\b/i.test(c)) { const cmd = cmdForScript('typecheck', scripts); if (cmd) return cmd; }
  if (/\b(tests?|smoke|specs?)\b/i.test(c)) { const cmd = cmdForScript('test', scripts); if (cmd) return cmd; }
  if (/\bbuild\b/i.test(c)) { const cmd = cmdForScript('build', scripts); if (cmd) return cmd; }
  return null;
}

/**
 * Compile a task's acceptance into a single verifier fn, or null when nothing is runnable.
 * The verifier runs every extracted command via runCheck and is GREEN iff all pass.
 *
 * @param {object} task — { acceptance: string|string[], ... }
 * @param {object} deps — { root, runCommand, scripts }
 * @returns {(()=>Promise<{passed:boolean,evidence:string}>)|null}
 */
export function compileAcceptance(task, { root, runCommand, scripts } = {}) {
  const criteria = normalizeAcceptance(task);
  const cmds = [];
  for (const c of criteria) {
    const cmd = extractCommand(c, scripts);
    if (cmd && !cmds.includes(cmd)) cmds.push(cmd);
  }
  if (cmds.length === 0) return null; // freeform/unrunnable ⇒ caller falls back to E1 verifiers

  return describeVerifier(async () => {
    const failures = [];
    for (const cmd of cmds) {
      const r = await runCheck({ root, runCommand, cmd });
      if (!r.passed) failures.push(`[${cmd}] ${r.evidence || 'failed'}`);
    }
    if (failures.length === 0) return { passed: true, evidence: `acceptance ok (${cmds.length} check${cmds.length > 1 ? 's' : ''})` };
    return { passed: false, evidence: failures.slice(0, 5).join('\n') };
  }, { kind: 'acceptance', root, commands: cmds });
}

/**
 * Build the verifiers map for ONE task. If the task's acceptance compiles to a runnable
 * verifier, that is the authoritative gate (registered under 'smoke' so runOracle picks it
 * up — runOracle only iterates VERIFIER_NAMES). Otherwise fall back to E1's project
 * verifiers. `previewConsole` (optional) is always added when provided. PURE.
 *
 * @param {object} deps — { task, root, runCommand, scripts, previewConsole?, buildDefaultVerifiers }
 *   buildDefaultVerifiers is injected (from verifierFactories) to keep this module's import
 *   graph minimal and the fallback identical to E1.
 * @returns {object} a runOracle-compatible verifiers map
 */
export function verifiersForTask({ task, root, runCommand, scripts, previewConsole = null, buildDefaultVerifiers, runCommandFlag = false }) {
  const compiled = compileAcceptance(task, { root, runCommand, scripts });
  if (compiled) {
    const v = { smoke: compiled };
    if (typeof previewConsole === 'function') v.console = previewConsole;
    return v;
  }
  if (runCommandFlag && scripts && typeof runCommand === 'function') {
    const cmds = [];
    if (scripts.test) cmds.push(cmdForScript('test', scripts));
    if (scripts.lint) cmds.push(cmdForScript('lint', scripts));
    const valid = cmds.filter(Boolean);
    if (valid.length > 0) {
      const verifier = async () => {
        const failures = [];
        for (const cmd of valid) {
          const r = await runCheck({ root, runCommand, cmd });
          if (!r.passed) failures.push(`[${cmd}] ${r.evidence || 'failed'}`);
        }
        if (failures.length === 0) return { passed: true, evidence: `project checks ok (${valid.length})` };
        return { passed: false, evidence: failures.slice(0, 5).join('\n') };
      };
      const v = { smoke: verifier };
      if (typeof previewConsole === 'function') v.console = previewConsole;
      return v;
    }
  }
  if (typeof buildDefaultVerifiers === 'function') {
    return buildDefaultVerifiers({ root, runCommand, scripts, previewConsole });
  }
  const v = {};
  if (typeof previewConsole === 'function') v.console = previewConsole;
  return v;
}
